import React, {Component} from 'react';
import Resize from 'react-resize-detector';
import PropTypes from 'prop-types';

import * as d3 from 'd3';
import _ from 'lodash';

import HoverOver from '../HoverOver';
import './HeatBarChart.css';

class HeatBarChart extends Component {

  constructor(props) {
    super(props);

    this.state = {
      margin: {left: 25, right: 5, top: 20, bottom: 35},
      width: 500,
      height: props.height || 300
    };
  }

  componentDidMount() {
    this.handelResize();
  }

  componentDidUpdate() {
    setTimeout(this.renderD3.bind(this), 100);
  }

  handelResize() {
    const bounds = d3.select(this.refs.wrapper).node().getBoundingClientRect();
    const width = bounds.width || 100;
    const height = this.props.height || bounds.width * .87;

    let mergedState = _.merge({}, this.state, {
      width: width,
      height: height
    });

    this.setState(mergedState);
  }

  renderD3() {
    const width = this.state.width - this.state.margin.left - this.state.margin.right;
    const height = this.state.height - this.state.margin.top - this.state.margin.bottom;
    const props = this.props;
    const self = this;

    const valueX = row => _.get(row, props.dimX, 0);
    const valueY = row => _.get(row, props.dimY, 0);

    const valuesX = _.chain(props.data).map(valueX).uniq().value();
    const maxY = Math.max(10, d3.max(props.data, valueY));

    const x = d3.scaleBand().domain(valuesX).rangeRound([0, width]).paddingInner(0.18);
    const y = d3.scaleLinear().domain([1, maxY]).range([height, 0]).rangeRound([height, 0]);
    const axisY = d3.axisLeft(y).tickSizeInner(width, 0).tickSizeOuter(0).ticks(4);

    const rounding = d => {
      return Math.max(1, Math.min(x.bandwidth() / 2, (height - y(valueY(d))) / 2));
    };

    const heatScale = d3.scaleLinear()
      .domain([2, 7, 14, 21, 28])
      .range(['#e8e8e8', '#e4acac', '#c85a5a', '#985356', '#5d434c', '#31282b']);

    // Add the Y Axis only if no bar labels
    d3.select(this.refs.yAxis)
      .transition()
      .duration(300)
      .call(axisY);

    // Add bars
    const bars = d3.select(this.refs.bars)
      .selectAll("rect")
      .data(props.data);

    bars.enter()
      .append("rect")
      .attr("y", height - x.bandwidth())
      .attr("height", x.bandwidth())
      .each(addBars);

    bars.each(addBars);
    bars.exit().remove();

    // Adds bar rectangles to selection
    function addBars() {
      d3.select(this)
        .attr("class", function (d) {
          return 'heat-bar HEAT_' + valueX(d);
        })
        .attr("rx", rounding)
        .attr("ry", rounding)
        .attr("x", function (d) {
          return x(valueX(d));
        })
        .attr("fill", function (d) {
          return heatScale(+valueX(d))
        })
        .attr("width", x.bandwidth())
        .transition()
        .duration(300)
        .attr("y", function (d) {
          return y(valueY(d));
        })
        .attr("height", function (d) {
          const v = valueY(d);
          return Math.max(0, height - y(v));
        })
    }

    // Add hovers
    const hovers = d3.select(this.refs.hovers)
      .selectAll("rect")
      .data(props.data);

    hovers.enter()
      .append("rect")
      .attr("y", height - x.bandwidth())
      .attr("height", x.bandwidth())
      .each(addHovers);

    hovers.each(addHovers);
    hovers.exit().remove();

    // Adds bar rectangles to selection
    function addHovers() {
      d3.select(this)
        .attr("class", 'heat-bar-hover')
        .attr("x", function (d) {
          return x(valueX(d));
        })
        .attr("width", x.bandwidth())
        .on('mouseenter', d => {
          const p = {dimY: props.dimY, titleY: 'Counties In Range'};
          const cx = x(valueX(d)) + x.bandwidth() / 2;
          const cy = y(valueY(d));
          self.setState({hover: {coords: [cx, cy], props: p, data: d}});
          d3.select('.HeatMap').attr("class", "HeatMap highlight-" + valueX(d));
          d3.select('.HeatBarChart').attr("class", "HeatBarChart highlight-" + valueX(d));
        })
        .on("mouseout", d => {
          self.setState({hover: null});
          d3.select('.HeatMap').attr("class", "HeatMap");
          d3.select('.HeatBarChart').attr("class", "HeatBarChart");
        })
        .attr("y", 0)
        .attr("height", height)
    }

    const values = d3.select(this.refs.labels)
      .selectAll("text")
      .data(props.data);

    values.enter().append("text").each(addValues);
    values.each(addValues);
    values.exit().remove();

    function addValues() {
      d3.select(this)
        .attr("text-anchor", "middle")
        .attr("class", "bar-label")
        .text(d => valueX(d) === 32 ? '+' : valueX(d))
        .attr("x", d => {
          return x(valueX(d)) + x.bandwidth() / 2;
        })
        .transition()
        .attr("y", d => {
          return height - rounding(d) + 1;
        })
        .attr("opacity", d => {
          return (height - y(valueY(d)) < 20) ? 0 : 1;
        })
    }
  }

  render() {
    return (
      <div ref="wrapper" className="HeatBarChart" height={this.state.height}>
        <Resize handleWidth onResize={this.handelResize.bind(this)}/>
        <svg width={this.state.width} height={this.state.height}>
          <g transform={"translate(" + this.state.margin.left + "," + this.state.margin.top + ")"} ref="svg">
            <g className="axis">
              <g ref="yAxis"
                 transform={"translate(" + (this.state.width - this.state.margin.left - this.state.margin.right) + ",0)"}></g>

              <text ref="xTitle" className="axis-title"
                    textAnchor="middle"
                    x={this.state.width / 2 - this.state.margin.left}
                    y={this.state.height - this.state.margin.top - 12}>Overdose Death Rates per 100k Population
              </text>
            </g>
            <g ref="bars"></g>
            <g ref="labels"></g>
            <g ref="hovers"></g>
          </g>
        </svg>
        <HoverOver data={this.state.hover} {...this.state.margin}/>
      </div>
    );
  }
}

HeatBarChart.propTypes = {
  dimX: PropTypes.string,
  dimY: PropTypes.string,
  data: PropTypes.array,
};

export default HeatBarChart;


// WEBPACK FOOTER //
// ./src/charts/HeatBarChart/index.js